import { useSuspenseQuery } from '@tanstack/react-query'
import {
  messageQueryOptions,
  conversationQueryOptions,
  getIntegrationByNameQueryOptions,
  listConversationUsersQueryOptions,
} from '~/queries'
import { fullDateFormat, copyToClipboard } from '~/utils'
import { Badge, Icon } from '~/elements'
import { DateTime } from 'luxon'
import { Identifier, UserBadge } from '~/components'
import { useChildMatches, useRouterState, useNavigate } from '@tanstack/react-router'
import {
  Card,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@bpinternal/ui-kit-next'
import { Conversation, Integration, User } from '@botpress/client'
import { useState, useEffect, PropsWithChildren } from 'react'
import '~/features/conversations/components/styles.css'
import { Check } from 'lucide-react'
import { MessageBoard } from './MessageBoard'

type Props = PropsWithChildren<{
  workspaceId: string
  botId: string
  conversationId: string
}>

export const ConversationInfo = ({ children, ...props }: Props) => {
  const { conversation } = useSuspenseQuery(conversationQueryOptions(props)).data
  const { users } = useSuspenseQuery(listConversationUsersQueryOptions(props)).data
  const integration = useSuspenseQuery(getIntegrationByNameQueryOptions({ name: conversation.integration })).data

  const childMatches = useChildMatches()
  const firstMatch = childMatches.at(0)
  const messageId = firstMatch?.params && 'messageId' in firstMatch.params ? firstMatch.params.messageId : undefined

  return (
    <div className="flex h-full w-full flex-col gap-6">
      <DetailsSection integration={integration} users={users} conversation={conversation} />
      <TagsSection conversation={conversation} />
      <SummarySection conversation={conversation} />
      <MessageBoard
        className="-mx-5 -mb-5 flex-1 dark:bg-neutral-100 md:m-0"
        workspaceId={props.workspaceId}
        botId={props.botId}
        conversationId={props.conversationId}
      />
      {messageId && (
        <MessageDialog messageId={messageId} {...props}>
          {children}
        </MessageDialog>
      )}
    </div>
  )
}

type DetailsSectionProps = {
  integration: Integration
  users: User[]
  conversation: Conversation
}

const DetailsSection = ({ integration, users, conversation }: DetailsSectionProps) => {
  const isCompleted = DateTime.now().diff(DateTime.fromISO(conversation.updatedAt), 'hours').hours >= 24

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(10rem,1fr))] gap-x-6 gap-y-8">
        <DetailsBlock label="Participants">
          {(users?.length ?? 0) > 0 && (
            <div className="flex flex-wrap gap-2">
              {users?.map((user) => <UserBadge className="truncate" key={user.id} user={user} />)}
            </div>
          )}
        </DetailsBlock>
        <DetailsBlock label="Last Activity">
          <p className="text-sm">{fullDateFormat(conversation.updatedAt)}</p>
        </DetailsBlock>
        <DetailsBlock label="Integration">
          <div className="flex items-center gap-2">
            <Icon size="4" icon={(props) => <img src={integration.iconUrl} {...props} />} />
            <p className="text-sm">{integration.title}</p>
          </div>
        </DetailsBlock>
        <DetailsBlock label="Date Created">
          <p className="text-sm">{fullDateFormat(conversation.createdAt)}</p>
        </DetailsBlock>
        <DetailsBlock label="Conversation ID">
          <Identifier
            className="justify-start truncate"
            id={conversation.id}
            logsStartDate={conversation.createdAt}
            truncate={true}
          />
        </DetailsBlock>
        <DetailsBlock label="Status">
          <Badge
            variant="secondary"
            className={isCompleted ? 'w-min bg-green-100 text-green-700' : 'w-min bg-blue-100 text-blue-700'}
          >
            {isCompleted ? 'Completed' : 'Active'}
          </Badge>
        </DetailsBlock>
        {conversation.channel !== 'channel' && (
          <DetailsBlock label="Channel">
            <Badge variant="secondary" className="w-min">
              {conversation.channel}
            </Badge>
          </DetailsBlock>
        )}
      </div>
    </div>
  )
}

const DetailsBlock = ({ label, children }: React.PropsWithChildren<{ label: string }>) => (
  <div className="flex flex-col gap-2">
    <p className="text-sm font-medium text-neutral-500">{label}</p>
    {children}
  </div>
)

type TagsSectionProps = {
  conversation: Conversation
}
const TagsSection = ({ conversation }: TagsSectionProps) => {
  const hiddenTags = ['summary', 'title', 'sentiment', 'isDirty']
  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-neutral-500">Tags</span>
      <div className="flex flex-wrap gap-4">
        {Object.entries(conversation.tags)
          .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
          .map(
            ([key, value]) =>
              value &&
              !hiddenTags.includes(key) && (
                <Badge key={key} variant="secondary" className="flex max-w-96 gap-2">
                  <p className="font-medium">{key}</p>
                  <p className="line-clamp-1 font-normal text-muted-foreground">{value}</p>
                </Badge>
              )
          )}
      </div>
    </div>
  )
}

type SummarySectionProps = {
  conversation: Conversation
}
const SummarySection = ({ conversation }: SummarySectionProps) => {
  const isPending = conversation.tags.isDirty === 'true'
  const hasSummary = !!conversation.tags.summary
  return (
    (hasSummary || isPending) && (
      <Card className="ai-summary flex flex-col gap-2 p-6">
        {hasSummary ? (
          <>
            <h1 className="font-medium">AI Summary</h1>
            <p className="text-sm font-normal">{conversation.tags.summary}</p>
          </>
        ) : (
          <>
            <p className="text-sm font-normal text-muted-foreground">
              Your conversation summary will be available soon. Please check back later.
            </p>
            <div className="h-4 w-56 rounded-full bg-accent"></div>
            <div className="h-4 w-40 rounded-full bg-accent"></div>
          </>
        )}
      </Card>
    )
  )
}

type MessageDialogProps = PropsWithChildren<{
  workspaceId: string
  botId: string
  conversationId: string
  messageId?: string
}>

const MessageDialog = ({ messageId, children, ...props }: MessageDialogProps) => {
  const navigate = useNavigate()
  const currentPath = useRouterState().location.pathname
  const [open, setOpen] = useState<boolean>(!!messageId && currentPath.includes(messageId))
  const [displayCheck, setDisplayCheck] = useState(false)
  const { message } = useSuspenseQuery(messageQueryOptions({ messageId: messageId ?? '', ...props })).data

  useEffect(() => {
    if (messageId && currentPath.includes(messageId)) {
      setOpen(true)
    }
  }, [currentPath, messageId])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="rounded-xl"
        onAnimationEnd={() => {
          if (!open) {
            setDisplayCheck(false)
            navigate({
              to: '/workspaces/$workspaceId/bots/$botId/conversations/$conversationId',
              params: { ...props },
              search: (prev) => prev,
              resetScroll: false,
            })
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Message JSON</DialogTitle>
        </DialogHeader>
        <div className="flex gap-2">{children}</div>
        <DialogFooter className="flex flex-col gap-2">
          <Button
            variant="secondary"
            className="flex gap-1"
            onClick={(e) => {
              setDisplayCheck(true)
              copyToClipboard(JSON.stringify(message), 'Message JSON', {
                preventDefault: false,
                event: e,
                showToast: false,
              })
            }}
          >
            {displayCheck ? (
              <>
                Copied
                <Check />
              </>
            ) : (
              'Copy to clipboard'
            )}
          </Button>
          <DialogClose asChild>
            <Button>Close</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
